import { getToken } from "./Auth0Authenticator";
import { getCacheKeyPrefix } from "./parentTokenRelay";
import Utils from "./Utils";

const REQUEST_AUTH_CACHE = "REQUEST_AUTH_CACHE";
const AUTH_CACHE_RESPONSE = "AUTH_CACHE_RESPONSE";

const getCacheEntry = (): { cacheKey: string | null; cacheEntry: any } => {
  const prefix = getCacheKeyPrefix();
  const cacheKey =
    (prefix &&
      Object.keys(localStorage).find((key) => key.startsWith(prefix))) ||
    null;
  if (!cacheKey) return { cacheKey: null, cacheEntry: null };

  try {
    const raw = localStorage.getItem(cacheKey);
    const cacheEntry = raw ? JSON.parse(raw) : null;
    // Refresh tokens rotate on use and Auth0 flags reuse as a security
    // violation, invalidating the whole token family - IMAS's own
    // Auth0Authenticator.ts already guards against exactly this by
    // serializing refresh calls across same-origin tabs (see acquireToken).
    // Relaying the same refresh token to a different origin has no such
    // coordination and risks IMAS consuming/rotating it concurrently with
    // the child, invalidating both sessions. Only the (non-rotating, freely
    // reusable until expiry) access token is safe to hand across origins.
    if (cacheEntry && cacheEntry.body) {
      delete cacheEntry.body.refresh_token;
    }
    return { cacheKey, cacheEntry };
  } catch (e) {
    console.error(e);
    return { cacheKey: null, cacheEntry: null };
  }
};

// auth0-spa-js stores the ID token/user claims under a SEPARATE key from the
// access token entry above - `@@auth0spajs@@::{clientId}::@@user@@`, with no
// audience/scope segment (see CacheManager.getIdTokenCacheKey in
// @auth0/auth0-spa-js). getUser()/getIdTokenClaims() read only from this key;
// without it, a client can hold a perfectly valid cached access token and
// still resolve as unauthenticated because getUser() comes back undefined.
const getIdTokenCacheEntry = (): {
  idTokenCacheKey: string | null;
  idTokenCacheEntry: any;
} => {
  const clientId = Utils.getAuth0ClientIdByEnvironment("clientid");
  const idTokenCacheKey = `@@auth0spajs@@::${clientId}::@@user@@`;

  try {
    const raw = localStorage.getItem(idTokenCacheKey);
    if (!raw) return { idTokenCacheKey: null, idTokenCacheEntry: null };
    return { idTokenCacheKey, idTokenCacheEntry: JSON.parse(raw) };
  } catch (e) {
    console.error(e);
    return { idTokenCacheKey: null, idTokenCacheEntry: null };
  }
};

/**
 * IMAS is already authenticated by the time an embedded child (Outreach,
 * Debt) loads, but each child's own Auth0 cache lives in its own origin's
 * localStorage - it has no way to populate that cache from inside its
 * iframe. Relaying IMAS's own token/cache entry down over postMessage is
 * the only path back to an authenticated state without a top-level login,
 * which is blocked by frame-ancestors CSP inside a nested iframe anyway.
 *
 * expectedOrigin must be the child's own iframe origin, never '*' - this
 * carries a live access token, unlike the toast-only messages elsewhere.
 */
export const respondToAuthCacheRequest = async (
  event: MessageEvent,
  expectedOrigin: string,
): Promise<void> => {
  if (event.origin !== expectedOrigin) {
    return;
  }
  if (!event.data || event.data.type !== REQUEST_AUTH_CACHE) {
    return;
  }

  const source = event.source as Window | null;
  if (!source) {
    return;
  }

  const accessToken = await getToken();
  const { cacheKey, cacheEntry } = getCacheEntry();
  const { idTokenCacheKey, idTokenCacheEntry } = getIdTokenCacheEntry();

  source.postMessage(
    {
      type: AUTH_CACHE_RESPONSE,
      cacheKey,
      cacheEntry,
      idTokenCacheKey,
      idTokenCacheEntry,
      accessToken,
    },
    expectedOrigin,
  );
};
